// 404 page — noindex, with useful links so a wrong URL never becomes a "soft 404".
import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Home, BedDouble, Compass, Phone } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEOHead from '../components/SEOHead';
import { useLanguage } from '../context/LanguageContext';
import { homePath, sectionPath } from '../siteRoutes';

const TEXT = {
  en: ['Page not found', 'The page you were looking for does not exist or has moved. Here are some useful places to continue:'],
  me: ['Stranica nije pronađena', 'Stranica koju tražite ne postoji ili je premještena. Evo nekoliko korisnih linkova:'],
  sq: ['Faqja nuk u gjet', 'Faqja që kërkoni nuk ekziston ose është zhvendosur. Ja disa lidhje të dobishme:'],
  de: ['Seite nicht gefunden', 'Die gesuchte Seite existiert nicht oder wurde verschoben. Hier geht es weiter:'],
  ru: ['Страница не найдена', 'Запрошенная страница не существует или была перемещена. Полезные ссылки:'],
  pl: ['Strona nie została znaleziona', 'Szukana strona nie istnieje lub została przeniesiona. Przydatne linki:'],
  hu: ['Az oldal nem található', 'A keresett oldal nem létezik vagy áthelyezték. Hasznos linkek:'],
};

export default function NotFound() {
  const { lang, t } = useLanguage();
  const { pathname } = useLocation();
  const [title, lead] = TEXT[lang] || TEXT.en;
  const links = [
    [Home, t('nav.home'), homePath(pathname), 'home'],
    [BedDouble, t('nav.rooms'), sectionPath('rooms', pathname), 'rooms'],
    [Compass, t('guide.nav'), sectionPath('ulcinj-guide', pathname), 'ulcinj-guide'],
    [Phone, t('nav.contact'), sectionPath('contact', pathname), 'contact'],
  ];
  return (
    <div className="min-h-screen bg-gray-50" data-testid="not-found-page">
      <SEOHead noindex title={`${title} · Hotel La Rosa Negra`} description={lead} url={pathname} />
      <Header />
      <main className="container mx-auto px-4 pt-40 pb-24 max-w-3xl">
        <p className="text-orange-500 font-semibold text-sm uppercase tracking-wider mb-2">404</p>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-4" data-testid="not-found-title">{title}</h1>
        <p className="text-base md:text-lg text-gray-600 mb-10">{lead}</p>
        <nav className="grid sm:grid-cols-2 gap-3" aria-label="Suggested pages">
          {links.map(([Icon, label, to, id]) => (
            <Link key={id} to={to} className="flex items-center gap-3 rounded-xl border bg-white p-4 hover:border-orange-400 hover:-translate-y-0.5 transition-transform" data-testid={`not-found-link-${id}`}>
              <Icon className="text-orange-500" size={20} /><span className="font-semibold text-gray-800">{label}</span>
            </Link>
          ))}
        </nav>
      </main>
      <Footer />
    </div>
  );
}
